/**
 * English catalogue — the reference set. Every other locale falls back to this,
 * so any key added here should be added there too (or it simply shows English).
 *
 * Namespaces:
 *   ui.*      chrome: buttons, labels, dialogs, hints
 *   notice.*  system messages, including ones the server sends as key + params
 *   error.*   client-side failure text
 */
export default {
  // ---------------------------------------------------------------- gate
  'ui.tagline': 'Live rooms with audio & video',
  'ui.tab.guest': 'Join anonymously',
  'ui.tab.login': 'Sign in',
  'ui.tab.register': 'Register',
  'ui.field.nickname': 'Nickname',
  'ui.field.nicknamePlaceholder': 'pick any free nickname',
  'ui.field.room': 'Room',
  'ui.field.roomPlaceholder': "the server's default room",
  'ui.field.password': 'Password',
  'ui.field.repeatPassword': 'Repeat password',
  'ui.field.nicknameToClaim': 'Nickname to claim',
  'ui.gate.guestHint': 'Registered nicknames are protected — you cannot use one without its password.',
  'ui.gate.registerHint':
    'Registering reserves the nickname permanently — anonymous users will not be able to use it.',
  'ui.gate.keepSignedIn': 'Keep me signed in',
  'ui.gate.joinChat': 'Join chat',
  'ui.gate.signIn': 'Sign in',
  'ui.gate.createAccount': 'Create account',
  'ui.gate.language': 'Language',
  'ui.gate.checkingBrowser': 'Checking your browser…',
  'ui.gate.joining': 'Joining…',
  'ui.gate.connecting': 'Connecting…',
  'ui.gate.restoring': 'Restoring your session…',
  'ui.gate.signedOut': 'Signed out.',
  'ui.gate.passwordMismatch': 'The passwords do not match.',
  'ui.gate.nicknameAvailable': '"{nickname}" is available.',
  'ui.gate.nicknameUnavailable': 'That nickname is not available.',

  // ---------------------------------------------------------------- chrome
  'ui.brand': 'weppis',
  'ui.signOut': 'Sign out',
  // The topbar's ⋯ menu: appearance, supporting the server, and signing out.
  'ui.userMenu': 'Preferences',
  // Opens the server's Ko-fi page in a new tab.
  'ui.support': 'Support me on Ko-fi',
  // Tooltip on a nickname in the chat, which inserts it into the message box.
  'ui.mentionHint': 'Write to {nickname}',
  // The room bar's arrangement toggle. Each names the arrangement it switches
  // to, since that is what pressing the button does.
  'ui.stage.above': 'Show cameras above the chat',
  'ui.stage.beside': 'Show cameras beside the chat',
  // The bar along the stage's edge. Worded like the jukebox's, which does the
  // same job on the other split.
  'ui.stage.resize': 'Drag to resize the cameras — double-click to reset',
  // The ⋯ menu's account entry, and the dialog behind it. Only registered
  // users see it: a guest has no account to change.
  'ui.account': 'Account',
  'ui.account.title': 'Account settings',
  'ui.account.nickname': 'Nickname',
  'ui.account.nicknameHint': 'This is also what you sign in with. Changing it frees the old one for anyone else to take.',
  'ui.account.currentPassword': 'Current password',
  'ui.account.newPassword': 'New password',
  'ui.account.password': 'Password',
  'ui.account.changeNickname': 'Change nickname',
  'ui.account.changePassword': 'Change password',
  'ui.account.nicknameChanged': 'You are now known as {nickname}.',
  'ui.account.passwordChanged': 'Password changed.',
  'ui.theme': 'Appearance',
  'ui.theme.system': 'Follow system',
  'ui.theme.light': 'Light',
  'ui.theme.dark': 'Dark',
  'ui.theme.systemHint': 'Your system is currently set to {theme}.',
  'ui.theme.fluid': 'Fluid background',
  'ui.theme.fluidHint':
    'A fluid you can stir behind the transcript. It makes messages a little harder to read.',
  'ui.admin': 'Admin',
  'ui.rooms': 'Rooms',
  // Conversations, in the sidebar beside the rooms. "Direct messages" rather
  // than "Private messages": the server calls them private, but the word people
  // read here should say who they reach, not who cannot read them - and nothing
  // about this client claims they are encrypted.
  'ui.dm.heading': 'Direct messages',
  'ui.dm.itemLabel': 'Conversation with {nickname}',
  'ui.dm.unreadLabel': '{count} unread',
  'ui.dm.closeTitle': 'Close the conversation with {nickname}',
  // Shown where a room would show its topic.
  'ui.dm.subtitle': 'Only the two of you can see this.',
  'ui.dm.empty': 'Nothing said yet. Write to {nickname} below.',
  'ui.dm.placeholder': 'Message {nickname}',
  'ui.members': 'Members',
  'ui.membersIn': 'In #{room}',
  'ui.newRoom': 'Join or create a room',
  'ui.showSidebar': 'Show rooms and members',
  'ui.hideSidebar': 'Hide rooms and members',
  'ui.roomActions': 'Room actions',
  'ui.noRoom': 'no room',
  'ui.noTopic': 'No topic set.',
  'ui.joinToStart': 'Join a room to start chatting.',
  'ui.joinFromSidebar': 'Join a room from the sidebar to begin.',
  'ui.hiddenRoomSuffix': 'hidden room',
  'ui.badge.hidden': 'hidden',
  // Somebody this reader has hidden, as opposed to the badge above, which is
  // the server hiding root from everybody.
  'ui.badge.hiddenByYou': 'muted',
  'ui.badge.anonymous': 'anonymous',
  'ui.role.root': 'root',
  'ui.role.operator': 'operator',
  'ui.role.user': 'registred',
  'ui.role.guest': 'anonymous',

  // ---------------------------------------------------------------- composer
  'ui.mic': 'Mic',
  'ui.mute': 'Mute',
  'ui.unmute': 'Unmute',
  'ui.talk': "Hold to talk",
  'ui.talk.hint': "Hold to open your microphone",
  'ui.talk.live': "Talking \u2014 release to mute",
  'ui.settings.notifications': 'Notifications',
  'ui.settings.notificationSound': 'Play a sound for mentions and private messages',
  'ui.settings.notificationSoundHint':
    'A short chime when someone names you or messages you privately. It follows the speaker and level chosen here, and stays silent while everything is muted.',
  'ui.settings.talking': "Talking",
  'ui.settings.pushToTalk': "Push to talk",
  'ui.settings.pushToTalkHint': "Your microphone stays muted and opens only while you hold the Talk button. It replaces the Mute button.",
  'ui.camera': 'Camera',
  'ui.settings': 'Settings',
  // The composer's emote picker, opened by the :hymy: button.
  'ui.emotes': 'Emotes',
  'ui.attach.title': 'Attach an image or video',
  // The paperclip's menu, on phones. "Choose a file" is first because it is
  // what the paperclip did on its own before the menu existed.
  'ui.attach.chooseFile': 'Choose a file',
  'ui.attach.takePhoto': 'Take a photo',
  'ui.attach.recordVideo': 'Record a video',
  'ui.attach.uploading': 'Uploading {name}… {percent}%',
  'ui.attach.tooMany': 'One file at a time, please.',
  'ui.attach.openInNewTab': 'Open {name} in a new tab',
  'ui.attach.video': 'Video',
  'ui.attach.expired': 'Attachment expired.',
  'ui.attach.image': 'Image',
  'ui.send': 'Send',
  'ui.messagePlaceholder': 'Message, or /help for commands',
  'ui.messagePlaceholderMax': 'Message (max {max}), or /help for commands',
  'ui.messagePlaceholderOffline': 'Reconnecting — messages cannot be sent right now',
  'ui.mic.start': 'Start sending audio',
  'ui.mic.stop': 'Stop sending audio',
  'ui.mute.on': 'Mute your microphone (stay in the call)',
  'ui.mute.off': 'Unmute your microphone',
  'ui.mute.needsMic': 'Turn on your microphone first',
  'ui.camera.on': 'Turn on your camera and microphone',
  'ui.camera.off': 'Turn off your camera (and its microphone)',
  'ui.media.unavailable': 'Audio/video is unavailable here',

  // ---------------------------------------------------------------- connection
  'ui.conn.open': 'connected',
  'ui.conn.connecting': 'connecting…',
  'ui.conn.reconnecting': 'reconnecting…',
  'ui.conn.closed': 'closed',
  'ui.conn.lostRetryIn': 'Connection lost. Reconnecting in {seconds}s…',
  'ui.conn.lostRetrying': 'Connection lost. Reconnecting…',
  'ui.conn.connectingBanner': 'Connecting…',
  'ui.conn.offline': 'Not connected.',
  'ui.conn.retryNow': 'Retry now',

  /*
   * The update notice. Worded as an offer rather than an instruction: the page
   * is a conversation somebody may be in the middle of, and the rooms they have
   * open come back by themselves after a refresh, so there is nothing to lose
   * by waiting and nothing to gain by nagging.
   */
  'ui.update.available': 'A new version of the chat is ready. Refresh to load it.',
  'ui.update.refresh': 'Refresh',
  'ui.update.dismiss': 'Not now',

  // ---------------------------------------------------------------- dialogs
  'ui.cancel': 'Cancel',
  'ui.ok': 'OK',
  'ui.done': 'Done',
  'ui.close': 'Close',
  'ui.save': 'Save',
  'ui.newRoom.title': 'Join or create a room',
  'ui.newRoom.name': 'Room name',
  'ui.newRoom.namePlaceholder': 'room name',
  'ui.newRoom.join': 'Join',
  'ui.newRoom.hidden': 'Hidden room (not shown in the room list)',
  'ui.newRoom.hiddenHint':
    'A hidden room only applies if this creates the room. People can still join it if they know the name.',
  'ui.newRoom.nameRequired': 'Enter a room name.',

  'ui.room.summary': '#{room} — {count} present.',
  'ui.room.topicIs': 'Topic: {topic}',
  'ui.room.noTopicSet': 'No topic set.',
  'ui.room.isHidden': 'Hidden: it does not appear in the room list.',
  'ui.room.isPersistent': 'This room is permanent and cannot be removed.',
  'ui.room.setTopic': 'Set topic…',
  'ui.room.leave': 'Leave room',
  'ui.room.remove': 'Remove room…',
  'ui.room.hide': 'Hide from room list',
  'ui.room.show': 'Show in room list',
  'ui.room.onlyOperatorsRemove': 'Only operators can remove a room.',
  'ui.room.topicTitle': 'Topic for #{room}',
  'ui.room.topicLabel': 'Topic',
  'ui.room.topicPlaceholder': 'What is this room about?',
  'ui.room.topicClearHint': 'Leave it empty to clear the topic.',
  'ui.room.removeTitle': 'Remove #{room}?',
  'ui.room.removeWarning':
    'Removing #{room} disconnects {count} member(s) and deletes its history. This cannot be undone.',
  'ui.room.removeConfirmLabel': 'Type the room name to confirm',
  'ui.room.removeConfirmButton': 'Remove room',
  'ui.room.removeMismatch': 'Type "{room}" to confirm.',
  'ui.room.leaveTitle': 'Leave #{room}',

  // ---------------------------------------------------------------- jukebox
  // The room's shared YouTube player. An operator starts and steers it; anyone
  // in the room can put something in the queue.
  'ui.jukebox': 'Jukebox',
  'ui.jukebox.start': 'Start jukebox',
  'ui.jukebox.stop': 'Stop jukebox',
  // The composer's Jukebox button. "Hide" says what the press does; the second
  // clause is the part worth keeping from the settings switch this replaced,
  // since switching it off is also what keeps youtube.com out of the page.
  'ui.jukebox.hide': "Hide the room's jukebox — nothing is asked of youtube.com",
  'ui.jukebox.show': "Show and hear the room's jukebox",
  'ui.jukebox.play': 'Play',
  'ui.jukebox.pause': 'Pause',
  'ui.jukebox.skip': 'Next track',
  'ui.jukebox.add': 'Add a video',
  'ui.jukebox.remove': 'Remove from the queue',
  'ui.jukebox.volume': 'Jukebox volume',
  'ui.jukebox.mute': 'Mute the jukebox',
  'ui.jukebox.unmute': 'Unmute the jukebox',
  // The bar between the jukebox and the transcript.
  'ui.jukebox.resize': 'Drag to resize — double-click to even it up',
  'ui.jukebox.nothingPlaying': 'Nothing playing',
  'ui.jukebox.addSomething': 'Add something with +',
  'ui.jukebox.queuedBy': 'added by {nickname}',
  'ui.jukebox.queueCount': 'Queue · {count}',
  'ui.jukebox.queueEmpty': 'The queue is empty.',
  'ui.jukebox.addTitle': 'Add to the queue',
  'ui.jukebox.link': 'YouTube link',
  'ui.jukebox.addHint':
    'The address of a video, or its id. Everyone in the room hears it at the same time.',
  'ui.jukebox.addConfirm': 'Add to queue',
  'ui.jukebox.linkRequired': 'Paste a YouTube link.',
  'ui.jukebox.queued': '"{title}" was added to the queue.',
  'ui.jukebox.playerFailed': 'The YouTube player could not be loaded.',

  'ui.member.summary': '{nickname} — {kind}{role}.',
  'ui.member.guest': 'anonymous guest',
  'ui.member.registered': 'registered account',
  'ui.member.roleRoot': ', root administrator',
  'ui.member.roleOperator': ', operator',
  'ui.member.onDevices': ', on {count} devices',
  'ui.member.hiddenFromOthers': ' — hidden from everyone else',
  'ui.member.activateForActions': '{description}. Activate for actions.',
  'ui.member.privateMessage': 'Private message',
  'ui.member.hide': 'Hide {nickname}',
  'ui.member.unhide': 'Show {nickname} again',
  'ui.member.isHiddenByYou': 'You have hidden them.',
  /*
   * The hide list. Worded to make clear it is this browser's and nobody else's:
   * "hidden" on its own reads like a moderation action, which this is not.
   */
  'ui.hidden.manage': 'Hidden people',
  'ui.hidden.title': 'People you have hidden',
  'ui.hidden.summary':
    'Their messages are kept out of your chat. This is yours alone — nobody is told, and it applies in every room on this browser. {count} hidden.',
  'ui.hidden.empty':
    'You have not hidden anybody. Choose someone in the member list to hide them; their messages stop appearing in your chat, and nobody else is affected.',
  'ui.hidden.unhide': 'Show',
  'ui.hidden.clear': 'Show everyone again',

  'ui.member.whois': 'Whois',
  'ui.member.stopMic': 'Stop mic',
  'ui.member.stopVideo': 'Stop video',
  'ui.member.kick': 'Kick',
  'ui.member.ban': 'Ban…',
  'ui.member.makeOperator': 'Make operator',
  'ui.member.removeOperator': 'Remove operator',

  'ui.kick.title': 'Kick {nickname}',
  'ui.kick.scope': 'Scope',
  'ui.kick.fromRoom': 'From #{room}',
  'ui.kick.fromServer': 'Disconnect from the server',
  'ui.kick.reason': 'Reason',
  'ui.kick.reasonPlaceholder': 'optional reason',
  'ui.kick.hint':
    'A kick is temporary — they can come back straight away. Their messages are removed.',
  'ui.kick.confirm': 'Kick',

  'ui.ban.title': 'Ban {nickname}',
  'ui.ban.fromRoom': 'From #{room}',
  'ui.ban.fromServer': 'From the whole server',
  'ui.ban.duration': 'Duration',
  'ui.ban.durationPlaceholder': 'e.g. 30m, 2h, 7d — blank = indefinite',
  'ui.ban.timed': 'Timed ban: {duration}.',
  'ui.ban.permanentWarning': 'No duration given — this will be a PERMANENT ban.',
  'ui.ban.invalidDuration':
    '"{value}" is not a duration. Use 30m, 2h, 7d, a number of minutes, or leave it blank for a permanent ban.',
  'ui.ban.confirm': 'Ban',

  'ui.operator.grantTitle': 'Make operator: {nickname}',
  'ui.operator.revokeTitle': 'Remove operator: {nickname}',
  'ui.operator.scope': 'Scope',
  'ui.operator.scopePlaceholder': 'blank = server-wide, or #room',
  'ui.operator.grantHint':
    'Give {nickname} operator privileges: they will be able to kick and ban users. Leave the scope blank for server-wide.',
  'ui.operator.revokeHint':
    "Remove {nickname}'s operator privileges. Leave the scope blank to remove server-wide status.",
  'ui.operator.grant': 'Grant',
  'ui.operator.revoke': 'Remove',

  // ---------------------------------------------------------------- settings
  'ui.settings.title': 'Audio & video settings',
  'ui.settings.microphone': 'Microphone',
  'ui.settings.camera': 'Camera',
  'ui.settings.speakers': 'Speakers',
  'ui.settings.systemDefault': 'System default',
  'ui.settings.volume': 'Volume of incoming audio and video',
  'ui.settings.volumeHint':
    'Covers everyone in the room and the jukebox. Each participant also has their own slider on their video tile, and the jukebox one of its own.',
  // The mute on the volume icon in the media bar: everything incoming at once.
  'ui.settings.muteAll': 'Mute everything you are hearing',
  'ui.settings.unmuteAll': 'Unmute',
  'ui.settings.muted': 'muted',
  'ui.settings.quality': 'Camera quality',
  'ui.settings.quality360': '360p — lowest bandwidth',
  'ui.settings.quality480': '480p',
  'ui.settings.quality720': '720p — recommended',
  'ui.settings.quality1080': '1080p — highest bandwidth',
  'ui.settings.shareScreen': 'Share screen',
  'ui.settings.stopSharing': 'Stop sharing',
  'ui.settings.testMic': 'Test microphone',
  'ui.settings.stopTest': 'Stop test',
  'ui.settings.language': 'Language',
  'ui.settings.deviceAccess': 'Allow device access to list microphones, cameras and speakers',
  'ui.settings.loadingDevices': 'Loading devices…',
  'ui.settings.needsAccess':
    'Your browser hides device names and speaker choices until it has been given access once. You do not have to broadcast anything.',
  'ui.settings.noOutputChoice':
    'This browser does not support choosing an output device; it follows the system default.',
  'ui.settings.accessRefused':
    'Access was refused, so the browser will keep the device list hidden. Playback follows the system default until you allow it.',
  'ui.settings.micFailed': 'Could not open the microphone: {error}',
  'ui.settings.volumeFor': 'Volume for {nickname}',
  'ui.settings.muteFeed': 'Mute {nickname}',
  'ui.settings.unmuteFeed': 'Unmute {nickname}',
  // Shown on a feed the browser started silently, for want of a user gesture.
  'ui.tile.fullscreen': 'Full screen',
  'ui.tile.hideVideo': "Hide {nickname}'s video",
  'ui.tile.showVideo': "Show {nickname}'s video",
  'ui.newMessage': '1 new message ↓',
  'ui.newMessages': '{count} new messages ↓',
  'ui.tile.soundBlocked': '🔇 Click for sound',
  // Shown when the browser refused to start the picture at all (Firefox's
  // "Block Audio and Video" autoplay setting).
  'ui.tile.playBlocked': '▶ Click to play',

  // ---------------------------------------------------------------- admin
  'ui.admin.title': 'Administration',
  'ui.admin.operators': 'Operators',
  'ui.admin.bans': 'Bans',
  'ui.admin.audit': 'Audit log',
  'ui.admin.announce': 'Announce',
  'ui.admin.loading': 'Loading…',
  'ui.admin.who': 'Who',
  'ui.admin.scope': 'Scope',
  'ui.admin.status': 'Status',
  'ui.admin.online': 'online',
  'ui.admin.offline': 'offline',

  // ---- users (root only)
  'ui.admin.users': 'Users',
  'ui.admin.usersHint':
    'Every registered account on this server. Removing one is permanent: it signs the account out everywhere, removes what it said, and frees its nickname.',
  'ui.admin.userOnline': { one: 'online', other: 'online ({count} devices)' },
  'ui.admin.userDisabled': 'disabled',
  'ui.admin.userCreated': 'Registered',
  'ui.admin.userLastSignIn': 'Last signed in',
  'ui.admin.userNeverSignedIn': 'never',
  'ui.admin.userRemove': 'Remove',
  'ui.admin.userRemoveTitle': 'Remove {nickname}?',
  'ui.admin.userRemoveWarning':
    'This removes the account "{nickname}" for good. Everything it has said is deleted from every room, its operator privileges go with it, and any device still holding its session is signed out. This cannot be undone.',
  'ui.admin.userRemoveWarningOnline': {
    one: '"{nickname}" is connected right now and will be disconnected. Everything the account has said is deleted from every room, its operator privileges go with it, and it is signed out. This cannot be undone.',
    other:
      '"{nickname}" is connected on {count} devices right now and all of them will be disconnected. Everything the account has said is deleted from every room, its operator privileges go with it, and it is signed out. This cannot be undone.',
  },
  'ui.admin.userRemoveNickname':
    'The nickname "{nickname}" becomes free for anyone to claim, including anonymous visitors. Reserve it first with /reserve {nickname} if you would rather it stayed unavailable.',
  'ui.admin.userRemoveConfirmLabel': 'Type the nickname to confirm',
  'ui.admin.userRemoveMismatch': 'That does not match "{nickname}".',
  'ui.admin.hidden': 'hidden',
  'ui.admin.serverWide': 'server-wide',
  'ui.admin.serverWideRoot': 'server-wide (root)',
  'ui.admin.grantOperator': 'Grant operator',
  'ui.admin.revoke': 'Revoke',
  'ui.admin.rootOnlyOperators': 'Only the root administrator can change operator status.',
  'ui.admin.noOperators': 'No operators have been appointed yet.',
  'ui.admin.noBans': 'No active bans.',
  'ui.admin.banId': 'ID',
  'ui.admin.banType': 'Type',
  'ui.admin.banSubject': 'Subject',
  'ui.admin.banExpires': 'Expires',
  'ui.admin.banBy': 'By',
  'ui.admin.banReason': 'Reason',
  'ui.admin.banIndefinite': 'indefinite',
  'ui.admin.lift': 'Lift',
  'ui.admin.auditEmpty': 'The audit log is empty.',
  'ui.admin.announcement': 'Announcement',
  'ui.admin.announcePlaceholder': 'Message to broadcast (max 500)',
  'ui.admin.announceTarget': 'Target',
  'ui.admin.announceTargetPlaceholder': 'blank = every room on the server, or #room',
  'ui.admin.sendAnnouncement': 'Send announcement',

  // ---------------------------------------------------------------- notices
  'notice.reconnected': 'Reconnected.',
  'notice.disconnected': 'Disconnected from the server. Reload the page to reconnect.',
  'notice.joinRoomFirst': 'Join a room first.',
  'notice.micMuted': 'Microphone muted.',
  'notice.micUnmuted': 'Microphone unmuted.',
  'notice.micOff': 'Microphone off — you are no longer sending audio.',
  'notice.micChangeFailed': 'Could not change the microphone: {error}',
  'notice.nowKnownAs': 'You are now known as {nickname}.',
  'notice.joined': {
    one: '{nickname} joined.',
    other: '{nickname} joined.',
  },
  'notice.joinedGuest': '{nickname} (anonymous) joined.',
  'notice.left': '{nickname} left.',
  'notice.leftKicked': '{nickname} left (kicked by {actor}).',
  'notice.leftBanned': '{nickname} left (banned by {actor}).',
  'notice.renamed': '{from} is now known as {to}.',
  'notice.topicSet': '{actor} set the topic: {topic}',
  'notice.topicCleared': '{actor} cleared the topic.',
  'notice.mediaEnabled': '{actor} enabled audio/video in this room.',
  'notice.mediaDisabled': '{actor} disabled audio/video in this room.',
  'notice.youWereKicked': 'You were kicked from #{room} by {actor}{reason}.',
  'notice.youWereBanned': 'You were banned from #{room} by {actor} {until}{reason}.',
  'notice.youWereDisconnected': 'You were disconnected by {actor}{reason}.',
  'notice.youWereBannedServer': 'You were banned from this server by {actor} {until}{reason}.',
  'notice.roomClosed': '#{room} was closed by {actor}.',
  'notice.roomRemoved': '#{room} was removed by {actor}.',
  'notice.userHidden': '{nickname} is hidden. Their messages will not appear in your chat.',
  'notice.userUnhidden': '{nickname} is no longer hidden.',
  'notice.roomNowHidden': '#{room} is now hidden — it will not appear in the room list.',
  'notice.roomNowListed': '#{room} is listed again.',
  'notice.messagesRemoved': "{nickname}'s messages were removed{by}.",
  'notice.messagesRemovedBy': ' by {actor}',
  'notice.forceStopped': 'An operator stopped your {kinds}.',
  'notice.jukeboxOpened': '{actor} started the jukebox.',
  'notice.jukeboxClosed': '{actor} stopped the jukebox.',
  'notice.jukeboxUnplayable': '"{title}" could not be played and was skipped.',
  'notice.mediaReset': 'The media server restarted. Re-enable your camera or microphone to continue.',
  'notice.cameraWithoutMic':
    'Your camera is on, but no microphone could be opened. Check that your browser and your system both allow microphone access.',
  'notice.kicked': 'Kicked {nickname}{scope}{messages}.',
  'notice.kickedFromRoom': ' from #{room}',
  'notice.kickedFromServer': ' from the server',
  'notice.messagesAlsoRemoved': ', removing {count} message(s)',
  'notice.operatorGranted': '{nickname} is now an operator {scope}.',
  'notice.operatorRevoked': '{nickname} is no longer an operator {scope}.',
  'notice.scopeServerWide': 'server-wide',
  'notice.scopeRoom': 'in #{room}',
  'notice.announcementSent': 'Announcement sent to {scope} ({count} recipient(s)).',
  'notice.banLifted': 'Ban #{id} lifted.',
  'notice.roomRemovedResult': '#{room} was removed{members}.',
  'notice.userRemoved':
    '{nickname} was removed: {sessions} session(s) disconnected, {messages} message(s) deleted.',
  'notice.roomRemovedMembers': ', sending {count} member(s) away',
  'notice.repeated': '{text} (×{count})',
  'notice.untilDate': 'until {date}',
  'notice.indefinitely': 'indefinitely',

  // ---------------------------------------------------------------- errors
  'error.generic': 'Something went wrong.',
  'error.notConnected': 'Not connected.',
  'error.couldNotJoin': 'Could not join #{room}: {error}',
  'error.couldNotRejoin': 'Could not rejoin #{room}: {error}',
  'error.couldNotRestore': 'Could not reopen #{room}: {error}',
  'error.couldNotStart': 'Could not start {source}: {error}',
  'error.couldNotPublish': 'Could not publish {source}: {error}',
  'error.couldNotRestart': 'Could not restart {source}: {error}',
  'error.permissionDenied':
    'Permission denied. Browsers only allow camera and microphone access on localhost or over HTTPS.',
  'error.deviceGone':
    'Could not use the selected {device}. Pick another one in Settings.',
  'error.deviceMicrophone': 'microphone',
  'error.deviceCamera': 'camera',
  'error.mediaFailed': 'Media {direction} connection failed. Try enabling your device again.',
  'error.sessionEnded': 'Your session ended. Please sign in again.',
  'error.sessionRestoreFailed': 'Could not restore your session: {error}',
  'error.sessionExpired': 'Session expired.',
  // ---- notices the server sends as a key, rendered here in the reader's language
  'notice.userKicked': '{nickname} was kicked by {actor}.',
  'notice.userKickedReason': '{nickname} was kicked by {actor} ({reason}).',
  'notice.userBanned': '{nickname} was banned from {where} {expiry} by {actor}{reasonText}{messagesText}.',
  'notice.forceStoppedOther': "{actor} stopped {nickname}'s {kinds}.",
  'notice.youAreOperator': 'You were given operator privileges {scopeText} by {actor}.',
  'notice.youAreNoLongerOperator': 'Your operator privileges {scopeText} were removed by {actor}.',
  'notice.operatorAddedRoom': '{nickname} is now an operator in #{room}.',
  'notice.operatorRemovedRoom': '{nickname} is no longer an operator in #{room}.',

  // ---------------------------------------------------------- statistics
  'ui.stats.open': 'Statistics',
  'ui.stats.title': 'Statistics',
  'ui.stats.titleFor': 'Statistics for #{room}',
  'ui.stats.tab.activity': 'Activity',
  'ui.stats.tab.talkers': 'Talkers',
  'ui.stats.tab.words': 'Words',
  'ui.stats.tab.media': 'On air',
  'ui.stats.tab.jukebox': 'Jukebox',
  'ui.stats.tab.fun': 'Notable',
  /* Deliberately a colon-list rather than a sentence: three numbers vary here,
     and the plural runtime picks a form from one `count` only. */
  'ui.stats.summary': 'Over {days} days in #{room}: {nicknames} nicknames, {lines} lines.',
  'ui.stats.disabled': 'This server does not keep channel statistics.',
  'ui.stats.empty': 'Nothing has been recorded here yet. Statistics build up as the channel is used.',
  'ui.stats.nothingYet': 'Nothing recorded yet.',
  'ui.stats.nothingNotable': 'Nothing stands out yet — come back once the channel has more to say.',

  // ---- shared column headings
  'ui.stats.who': 'Who',
  'ui.stats.what': 'What',
  'ui.stats.when': 'When',
  'ui.stats.lines': 'Lines',
  'ui.stats.words': 'Words',
  'ui.stats.word': 'Word',
  'ui.stats.uses': 'Times used',
  'ui.stats.lastBy': 'Last said by',
  'ui.stats.mentions': 'Mentions',
  'ui.stats.link': 'Link',
  'ui.stats.total': 'Total',
  'ui.stats.sessions': 'Sessions',
  'ui.stats.duration': 'Duration',
  'ui.stats.started': 'Started',
  'ui.stats.length': 'Length',
  'ui.stats.added': 'Added',
  'ui.stats.track': 'Track',
  'ui.stats.tracks': 'Tracks',
  'ui.stats.artist': 'Channel',
  'ui.stats.timesQueued': 'Times queued',
  'ui.stats.wordsPerLine': 'Words per line',
  'ui.stats.charsPerLine': 'Characters per line',
  'ui.stats.lastSeen': 'Last seen',
  'ui.stats.today': 'Today',
  'ui.stats.yesterday': 'Yesterday',
  'ui.stats.daysAgo': { one: '{count} day ago', other: '{count} days ago' },

  // ---- activity
  'ui.stats.byHour': 'Activity through the day',
  'ui.stats.byQuarter': 'Busiest people by time of day',
  'ui.stats.hourTitle': '{hour}:00 — {count} lines',
  'ui.stats.peakHour': 'around {hour}:00',
  'ui.stats.trend': 'Lines per day',
  'ui.stats.trendTitle': '{day} — {count} lines',
  'ui.stats.trendBusiest': 'Busiest day {day}, with {count} lines',
  'ui.stats.trendAverage': '{count} lines a day on average',
  'ui.stats.trendQuiet': { one: '{count} day with nothing said', other: '{count} days with nothing said' },
  'ui.stats.trendAria':
    'Bar chart: lines per day over the last {days} days, {lines} in total, busiest on {day} with {peak}.',
  'ui.stats.showTable': 'Show the numbers',
  'ui.stats.day': 'Day',
  'ui.stats.alsoRan': 'These people were not quite so busy:',
  'ui.stats.omitted': {
    one: '{count} more nickname did not make the list.',
    other: '{count} more nicknames did not make the list.',
  },

  // ---- words
  'ui.stats.topWords': 'Most used words',
  'ui.stats.mostReferenced': 'Most referenced nicknames',
  'ui.stats.hotLinks': 'Hot links',

  // ---- on air
  'ui.stats.totalAirtime': 'Total time on air',
  'ui.stats.broadcasts': 'Broadcasts',
  'ui.stats.broadcasters': 'People who went live',
  'ui.stats.airtimeLeaders': 'Most time on air',
  'ui.stats.recentBroadcasts': 'Recent broadcasts',
  'ui.stats.noBroadcasts': 'Nobody has been on air here yet.',
  'ui.stats.live': 'Live',
  'ui.stats.source.mic': 'Microphone',
  'ui.stats.source.camera': 'Camera',
  'ui.stats.source.screen': 'Screen',

  // ---- jukebox
  'ui.stats.tracksQueued': 'Tracks queued',
  'ui.stats.uniqueTracks': 'Different tracks',
  'ui.stats.totalMusic': 'Total running time',
  'ui.stats.jukeboxLeaders': 'Who queues the music',
  'ui.stats.topTracks': 'Most queued tracks',
  'ui.stats.recentTracks': 'Recently queued',
  'ui.stats.runningTime': 'Running time',
  'ui.stats.noTracks': 'Nothing has been queued here yet.',
  'ui.stats.unnamedTrack': 'name not known',
  'ui.stats.unnamedTrackHint':
    'Video {videoId} — its name could not be looked up when it was queued. The server tries again in the background.',

  // ---- notable, and the channel's own averages
  'ui.stats.notable': 'Notable numbers',
  'ui.stats.channelAverages': 'Channel averages',
  'ui.stats.topicChanges': 'Topic changes',
  'ui.stats.recentTopics': 'Recent topics',
  'ui.stats.topicBy': 'set by {nickname}, {when} at {time}',

  /*
   * One sentence per leaderboard, with a second phrasing for the runner-up. The
   * tone follows the pisg tradition these are modelled on: teasing, and about
   * the numbers rather than the person.
   */
  'ui.stats.fun.questions': 'Is {nickname} confused, or just curious? {percent} of their lines were questions.',
  'ui.stats.fun.questions2': '{nickname} wondered a fair bit too — {percent} of the time.',
  'ui.stats.fun.yells': '{nickname} did most of the shouting, ending {percent} of their lines in an exclamation mark.',
  'ui.stats.fun.yells2': '{nickname} was excitable as well, {percent} of the time.',
  'ui.stats.fun.caps': '{nickname} seems to have left Caps Lock on: {percent} of their lines were IN CAPITALS.',
  'ui.stats.fun.caps2': "The channel's other shouter is {nickname} — capitals {percent} of the time.",
  'ui.stats.fun.smiles': '{nickname} brightens the place up: {percent} of their lines carried a smiley.',
  'ui.stats.fun.smiles2': '{nickname} can be cheerful too — smileys {percent} of the time.',
  'ui.stats.fun.frowns': '{nickname} is the gloomy one: {percent} of their lines had a sad face.',
  'ui.stats.fun.frowns2': '{nickname} was downcast {percent} of the time as well.',
  'ui.stats.fun.swears': '{nickname} has the foulest mouth here — swearing in {percent} of their lines.',
  'ui.stats.fun.swears2': '{nickname} cannot always keep it clean either: {percent} of the time.',
  /* The plain-count entries below carry `one` / `other` forms, selected by the
     runtime from `count`. Without them a channel with one slap in it reads
     "slapped other people 1 times". */
  'ui.stats.fun.slapsGiven': {
    one: '{nickname} is an aggressive sort, having slapped somebody just once!',
    other: '{nickname} is an aggressive sort, having slapped other people {value} times!',
  },
  'ui.stats.fun.slapsGiven2': {
    one: '{nickname} threw their weight around once too.',
    other: '{nickname} threw their weight around too, {value} times.',
  },
  'ui.stats.fun.slapsTaken': {
    one: 'Poor {nickname} — slapped once by somebody else.',
    other: 'Poor {nickname} — slapped {value} times by everyone else.',
  },
  'ui.stats.fun.slapsTaken2': {
    one: '{nickname} is out of favour as well, slapped once.',
    other: '{nickname} is out of favour as well, slapped {value} times.',
  },
  'ui.stats.fun.actions': {
    one: '{nickname} wanted everyone to know what they were up to, once.',
    other: '{nickname} wanted everyone to know what they were up to: {value} actions.',
  },
  'ui.stats.fun.actions2': {
    one: '{nickname} narrated themselves once as well.',
    other: '{nickname} narrated themselves {value} times as well.',
  },
  'ui.stats.fun.monologues': {
    one: '{nickname} likes the sound of their own voice — one monologue, running {longest} lines.',
    other: '{nickname} likes the sound of their own voice — {value} monologues, the longest running {longest} lines.',
  },
  'ui.stats.fun.monologues2': {
    one: 'Another lonely soul is {nickname}, with one monologue.',
    other: 'Another lonely soul is {nickname}, with {value} monologues.',
  },
  'ui.stats.fun.kicksTaken': {
    one: '{nickname} got shown the door once!',
    other: '{nickname} got shown the door {value} times!',
  },
  'ui.stats.fun.kicksTaken2': {
    one: '{nickname} followed close behind, kicked once.',
    other: '{nickname} followed close behind, kicked {value} times.',
  },
  'ui.stats.fun.kicksGiven': {
    one: '{nickname} is quick with the boot, having kicked somebody once.',
    other: '{nickname} is quick with the boot, having kicked {value} times.',
  },
  'ui.stats.fun.kicksGiven2': {
    one: '{nickname} tried being the boss too, with one kick.',
    other: '{nickname} tried being the boss too, with {value} kicks.',
  },
  'ui.stats.fun.opsGiven': {
    one: '{nickname} shared the power around, handing out operator once.',
    other: '{nickname} shared the power around, handing out operator {value} times.',
  },
  'ui.stats.fun.opsGiven2': {
    one: '{nickname} was generous as well, once.',
    other: '{nickname} was generous as well, {value} times.',
  },
  'ui.stats.fun.deopsGiven': {
    one: '{nickname} took it away again once.',
    other: '{nickname} took it away again {value} times.',
  },
  'ui.stats.fun.deopsGiven2': {
    one: '{nickname} demoted somebody once too.',
    other: '{nickname} demoted people {value} times too.',
  },
  'ui.stats.fun.joins': {
    one: '{nickname} has joined the channel once.',
    other: '{nickname} cannot decide whether to be here: they joined {value} times.',
  },
  'ui.stats.fun.joins2': {
    one: '{nickname} came back once as well.',
    other: '{nickname} kept coming back as well, {value} times.',
  },
  'ui.stats.fun.uploads': {
    one: '{nickname} brought a picture, posting one attachment.',
    other: '{nickname} brought the pictures, posting {value} attachments.',
  },
  'ui.stats.fun.uploads2': {
    one: '{nickname} shared one as well.',
    other: '{nickname} shared {value} as well.',
  },
  'ui.stats.fun.mostWords': {
    one: '{nickname} is the only one to have said anything at all — one word!',
    other: '{nickname} is clearly the fastest typist here, with {value} words!',
  },
  'ui.stats.fun.mostWords2': {
    one: '{nickname} is a close second on one word.',
    other: '{nickname} is a close second on {value} words.',
  },
  'ui.stats.fun.longestLines': '{nickname} writes record-breaking lines, averaging {value} characters each.',
  'ui.stats.fun.longestLines2': '{nickname} goes on a bit too, at {value} characters a line.',
  'ui.stats.fun.shortestLines': '{nickname} prefers to keep it brief — just {value} characters a line.',
  'ui.stats.fun.shortestLines2': '{nickname} is sparing with words as well: {value} characters a line.',
  'ui.stats.fun.mostWordsPerLine': '{nickname} packs the most in, at {value} words a line.',
  'ui.stats.fun.mostWordsPerLine2': '{nickname} writes full sentences too, {value} words a line.',

  // ---- expiry and durations, also used to render ban notices locally
  'expiry.permanent': 'permanently',
  'expiry.expired': 'expired',
  'expiry.for': 'for {duration}',
  'duration.d': '{value}d',
  'duration.h': '{value}h',
  'duration.m': '{value}m',
  'duration.s': '{value}s',
  // ---- strings owned by the bundled example plugin
  'plugin.welcome': 'Welcome to #{room}. Type /help to see what you can do.',
  'plugin.welcomeGuest':
    'Welcome to #{room}. Type /help to see what you can do, or register to reserve your nickname.',
};
